import _ from 'lodash'
import { useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PopupState, { bindMenu, bindTrigger, InjectedProps } from 'material-ui-popup-state'
import { CellContext, createColumnHelper, VisibilityState } from '@tanstack/react-table'
import { useSnackbar } from 'notistack'

import {
  Alert,
  Button,
  Chip,
  ChipProps,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
} from '@mui/material'
import ChairAltRoundedIcon from '@mui/icons-material/ChairAltRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import GroupAddIcon from '@mui/icons-material/GroupAdd'
import InputIcon from '@mui/icons-material/Input'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import OutputRoundedIcon from '@mui/icons-material/OutputRounded'
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import HistoryIcon from '@mui/icons-material/History'

import HeaderConfiguration from '@/components/ui/tables/HeaderConfiguration'
import Table from '@/components/ui/tables/Table'
import { useDeleteWaitingRoomMutation, useJoinWaitingRoomMutation, useLeaveWaitingRoomMutation } from '@/core/services/waiting-room'

import { Network } from '@/core/models/network.model'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser, updateMyProfile } from '@/redux/reducers/auth.reducer'

import { getApiErrorMessage } from '@/utils/errors'
import { isStaff } from '@/utils/type-guards'

export type QueuePatient = {
  id: string
  name?: string
  surname?: string
  position?: number
}

export type WaitingRoomData = {
  id: string
  isFavorite: boolean
  isOpening: boolean
  name: string
  network?: Network
  specialities: string[]
  price?: number
  allowedActions: Array<'join' | 'access' | 'leave' | 'delete' | 'edit' | 'reserve' | 'view-consultations'>
}

export type WaitingRoomsTableColumn = 'name' | 'network' | 'isFavorite' | 'specialities' | 'isOpening' | 'price' | 'patient'

type WaitingRoomsTableProps = {
  data: WaitingRoomData[]
  invisibleColumns?: Array<WaitingRoomsTableColumn>
  loading?: boolean
}

const columnHelper = createColumnHelper<WaitingRoomData>()

export function WaitingRoomsTable(props: WaitingRoomsTableProps) {
  const { data, invisibleColumns, loading = false } = props
  const { t } = useTranslation()

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('name', {
        header: t('table.waitingRooms.header.name').toString(),
      }),
      columnHelper.accessor('network', {
        header: t('table.waitingRooms.header.network').toString(),
        cell: NetworkCell,
      }),
      columnHelper.accessor('isFavorite', {
        header: t('table.waitingRooms.header.favorite').toString(),
        cell: ({ getValue }) => <Stack alignItems="center">{getValue() ? <StarRoundedIcon color="primary" /> : <StarBorderRoundedIcon />}</Stack>,
        meta: { align: 'center' },
      }),
      columnHelper.accessor('specialities', {
        header: t('table.waitingRooms.header.specialities').toString(),
        cell: SpecialitiesCell,
      }),
      columnHelper.accessor('isOpening', {
        header: t('table.waitingRooms.header.openingStatus').toString(),
        cell: IsOpeningCell,
        meta: { align: 'center' },
      }),
      columnHelper.accessor('price', {
        header: t('table.waitingRooms.header.price').toString(),
      }),
      columnHelper.display({
        id: 'actions',
        header: HeaderConfiguration,
        cell: ActionsCell,
        meta: {
          align: 'center',
          sxCell: { width: '50px', position: 'sticky', right: 0, borderLeft: '1px solid #efefef', zIndex: 1, background: '#FFF', minWidth: '50px' },
          sxHeader: { width: '50px', position: 'sticky', right: 0, borderLeft: '1px solid #efefef', zIndex: 10, background: '#f9fafb', minWidth: '50px' },
        },
      }),
    ]
  }, [t])

  const columnVisibility = useMemo(
    () =>
      invisibleColumns?.reduce((acc: VisibilityState, col: string) => {
        acc[col] = false
        return acc
      }, {}),
    [invisibleColumns]
  )

  return <Table columns={columns} data={data} disableGlobalSearch initialColumnVisibility={columnVisibility} loading={loading} />
}

function RequireJoinNetworkDialog({ networkId, open, onClose }: { networkId: string; open: boolean; onClose: () => void }) {
  const { t } = useTranslation()
  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <DialogContent>
        <Alert severity="info">{t('dialog.joinNetworkWaitingRoom.message')}</Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="neutral">
          {t('common:button.cancel.label')}
        </Button>
        <Button component={Link} to={`/networks/${networkId}`} variant="contained">
          {t('common:button.join.label')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function JoinWaitingRoomMenuItem({ popupState, waitingRoomData }: { popupState: InjectedProps; waitingRoomData: WaitingRoomData }) {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const authUser = useAppSelector(selectAuthUser)

  const [openJoinNetworkDialog, setOpenJoinNetworkDialog] = useState(false)

  const [joinWaitingRoom] = useJoinWaitingRoomMutation()

  const isNetworkMember = !!waitingRoomData.network?.members.find((member) => member === authUser?._id)

  const handleCloseDialog = () => {
    setOpenJoinNetworkDialog(false)
    popupState.close()
  }

  const handleJoinWaitingRoom = async () => {
    try {
      const waitingRoom = await joinWaitingRoom(waitingRoomData.id).unwrap()
      popupState.close()
      navigate(`/waiting-rooms/${waitingRoom._id}`)
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.waitingRoom.join.error')), { variant: 'error' })
    }
  }

  return (
    <>
      <MenuItem
        onClick={async () => {
          if (waitingRoomData.network && !isNetworkMember) {
            setOpenJoinNetworkDialog(true)
          } else {
            handleJoinWaitingRoom()
          }
        }}
      >
        <ListItemIcon>
          <GroupAddIcon />
        </ListItemIcon>
        <ListItemText>{t('table.waitingRooms.actions.joinWaitingRoom.tooltip')}</ListItemText>
      </MenuItem>
      {waitingRoomData.network && <RequireJoinNetworkDialog networkId={waitingRoomData.network._id} open={openJoinNetworkDialog} onClose={handleCloseDialog} />}
    </>
  )
}

function ReserveWaitingRoomMenuItem({ popupState, waitingRoomData }: { popupState: InjectedProps; waitingRoomData: WaitingRoomData }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const authUser = useAppSelector(selectAuthUser)
  const [openJoinNetworkDialog, setOpenJoinNetworkDialog] = useState(false)
  const isNetworkMember = !!waitingRoomData.network?.members.find((member) => member === authUser?._id)
  return (
    <>
      <MenuItem
        onClick={() => {
          if (waitingRoomData.network && waitingRoomData.network.privacy === 'private' && !isNetworkMember) {
            setOpenJoinNetworkDialog(true)
          } else {
            navigate(`/waiting-rooms/${waitingRoomData.id}/reserve`, { state: { background: location } })
            popupState.close()
          }
        }}
      >
        <ListItemIcon>
          <ChairAltRoundedIcon />
        </ListItemIcon>
        <ListItemText>{t('table.waitingRooms.actions.reserveConsultation.tooltip')}</ListItemText>
      </MenuItem>
      {waitingRoomData.network && (
        <RequireJoinNetworkDialog
          networkId={waitingRoomData.network._id}
          open={openJoinNetworkDialog}
          onClose={() => {
            setOpenJoinNetworkDialog(false)
            popupState.close()
          }}
        />
      )}
    </>
  )
}

function FavoriteWaitingRoomMenuItem({ popupState, waitingRoomId }: { popupState: InjectedProps; waitingRoomId: string }) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const authUser = useAppSelector(selectAuthUser)

  if (!isStaff(authUser)) return null

  const isFavorite = authUser.favoriteWaitingRooms?.includes(waitingRoomId)

  const handleToggleFavorite = () => {
    const currentFavorites = authUser.favoriteWaitingRooms ?? []
    const favoriteWaitingRooms = isFavorite ? currentFavorites.filter((id) => id !== waitingRoomId) : [...currentFavorites, waitingRoomId]
    dispatch(updateMyProfile({ favoriteWaitingRooms }))
    popupState.close()
  }

  return (
    <MenuItem onClick={handleToggleFavorite}>
      <ListItemIcon>{isFavorite ? <StarRoundedIcon color="primary" /> : <StarBorderRoundedIcon />}</ListItemIcon>
      <ListItemText>{isFavorite ? t('table.waitingRooms.actions.removeFromFavorites.label') : t('table.waitingRooms.actions.addToFavorites.label')}</ListItemText>
    </MenuItem>
  )
}

function ActionsCell({ row }: CellContext<WaitingRoomData, unknown>) {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const [deleteWaitingRoom] = useDeleteWaitingRoomMutation()
  const [leaveWaitingRoom] = useLeaveWaitingRoomMutation()

  const { allowedActions, id: waitingRoomId, isOpening } = row.original

  return (
    <Stack justifyContent="flex-end" direction="row" spacing={1}>
      <PopupState variant="popover" popupId={`waiting-room-${waitingRoomId}-actions`}>
        {(popupState) => (
          <>
            <IconButton {...bindTrigger(popupState)} data-testid={`waiting-room-${waitingRoomId}-actions-button`}>
              <MoreHorizIcon />
            </IconButton>
            <Menu {...bindMenu(popupState)} anchorOrigin={{ vertical: 'top', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
              <FavoriteWaitingRoomMenuItem popupState={popupState} waitingRoomId={waitingRoomId} />
              {allowedActions.includes('edit') && (
                <MenuItem component={Link} to={`/waiting-rooms/${waitingRoomId}/edit`}>
                  <ListItemIcon>
                    <EditRoundedIcon />
                  </ListItemIcon>
                  <ListItemText>{t('common:button.edit.label')}</ListItemText>
                </MenuItem>
              )}
              {isOpening && allowedActions.includes('reserve') && <ReserveWaitingRoomMenuItem popupState={popupState} waitingRoomData={row.original} />}
              {allowedActions.includes('join') && <JoinWaitingRoomMenuItem popupState={popupState} waitingRoomData={row.original} />}
              {allowedActions.includes('access') && (
                <MenuItem component={Link} to={`/waiting-rooms/${waitingRoomId}`}>
                  <ListItemIcon>
                    <InputIcon />
                  </ListItemIcon>
                  <ListItemText>{t('table.waitingRooms.actions.openWaitingRoom.label')}</ListItemText>
                </MenuItem>
              )}
              {allowedActions.includes('leave') && (
                <MenuItem
                  onClick={async () => {
                    try {
                      await leaveWaitingRoom(waitingRoomId).unwrap()
                      popupState.close()
                    } catch (error) {
                      enqueueSnackbar(getApiErrorMessage(error, t('notification.waitingRoom.leave.error')), { variant: 'error' })
                    }
                  }}
                >
                  <ListItemIcon>
                    <OutputRoundedIcon />
                  </ListItemIcon>
                  <ListItemText>{t('table.waitingRooms.actions.leaveWaitingRoom.tooltip')}</ListItemText>
                </MenuItem>
              )}
              {allowedActions.includes('delete') && (
                <MenuItem
                  onClick={async () => {
                    try {
                      await deleteWaitingRoom(waitingRoomId).unwrap()
                      popupState.close()
                    } catch (error) {
                      enqueueSnackbar(getApiErrorMessage(error, t('notification.waitingRoom.delete.error')), { variant: 'error' })
                    }
                  }}
                >
                  <ListItemIcon>
                    <DeleteRoundedIcon />
                  </ListItemIcon>
                  <ListItemText>{t('common:button.delete.label')}</ListItemText>
                </MenuItem>
              )}
              {allowedActions.includes('view-consultations') && row.original.network?._id && (
                <MenuItem
                  component={Link}
                  to={`/networks/${row.original.network._id}/waiting-rooms/${row.original.id}/consultations`}
                  onClick={() => {
                    popupState.close()
                  }}
                >
                  <ListItemIcon>
                    <HistoryIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{t('table.waitingRooms.actions.viewConsultations.label')}</ListItemText>
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </PopupState>
    </Stack>
  )
}

function IsOpeningCell({ getValue }: CellContext<WaitingRoomData, WaitingRoomData['isOpening']>) {
  const { t } = useTranslation()
  const color: ChipProps['color'] = getValue() ? 'success' : 'error'
  return (
    <Chip
      color={color}
      label={t(`common:model.waitingRoom.status.${getValue() ? 'open' : 'closed'}.label`)}
      size="small"
      variant="filled"
      sx={{ fontSize: '12px' }}
    />
  )
}

function NetworkCell({ getValue }: CellContext<WaitingRoomData, WaitingRoomData['network']>) {
  const { t } = useTranslation()
  const network = getValue()
  if (!network) return <>-</>
  return (
    <Stack alignItems="start" spacing={1}>
      <span>{network.name}</span>
      {network.privacy === 'public' && (
        <Chip color="info" label={t('common:model.network.privacy.public')} size="small" variant="filled" sx={{ fontSize: '12px' }} />
      )}
      {network.privacy === 'private' && (
        <Chip color="warning" label={t('common:model.network.privacy.private')} size="small" variant="filled" sx={{ fontSize: '12px' }} />
      )}
    </Stack>
  )
}

function SpecialitiesCell({ getValue, row }: CellContext<WaitingRoomData, WaitingRoomData['specialities']>) {
  const specialities = getValue()
  return (
    <Stack direction="row" flexWrap="wrap" gap={1}>
      {_.map(specialities, (speciality, index) => (
        <Chip key={`${row.original.id}-${speciality}-${index}`} label={speciality} variant="outlined" />
      ))}
    </Stack>
  )
}

export default WaitingRoomsTable
